import { Button, Col, Collapse, Form, Row, Typography } from "antd";
import { MazakaForm } from "apps/Common/MazakaForm";
import { getOptionsListByCategory } from "apps/CustomerPanel/Ad/Services";
import { openNotificationWithIcon } from "helpers/OpenNotificationWithIcon";
import useMazakaForm from "hooks/useMazakaForm";
import { FC, useEffect, useState, useCallback, useRef } from "react";
import Pricing from "./Pricing";
import BaseOptionItem from "./BaseOptions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/Reducers";
import {
  handleResetAdvertListFilter,
  handleSetAdvertListFilter,
} from "../ClientStates";
import { parsePrice } from "helpers/ParsePrice";
import { useCurrencies } from "apps/Common/Currency/ServerSideStates";
import Search from "./Search";
import ScrollButton from "./FilterScrollButton";
import { Input } from "antd";
import { ClearOutlined, FilterOutlined } from "@ant-design/icons";
import { MazakaButton } from "apps/Common/MazakaButton";
import RangeOptionsFilter from "./RangeOptionsFilter"

const LeftDetailsFilter: FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const { Panel } = Collapse;
  const { Text } = Typography;
  const [form] = Form.useForm();
  const [currencySegmentValue, setCurrencySegmentValue] = useState(1);
  const { formActions, mazakaForm } = useMazakaForm(form);
  const totalPanels = ["1", "2", "3", "4", "5"];
  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const [isShowAllPanel, setIsShowAllPanel] = useState(true);
  const [activeKeys, setActiveKeys] = useState<string[]>(totalPanels);
  const [options, setOptions] = useState<any>([]);
  
  // Range değerleri için state - UI için anlık güncelleme
  const [rangeValues, setRangeValues] = useState<Record<number, { min: any, max: any }>>({});
  
  // Debounce için ref
  const debounceTimeouts = useRef<Record<number, NodeJS.Timeout>>({});
  
  const { filter } = useSelector((state: RootState) => state.realState);
  const currencies = useCurrencies();
  const dispatch = useDispatch();

  // API çağrısı
  const getOptionListByCategory = async (categoryId: string) => {
    try {
      const response = await getOptionsListByCategory({CategoryId: categoryId});
      if (response?.data) {
        setOptions(response?.data);
      }
    } catch (error: any) {
      if (error?.code === 404) {
        setOptions([]);
      } else {
        openNotificationWithIcon("default", error?.message);
        console.log("somethings wrong during getting category", error?.message);
      }
    }
  };

  // Category değiştiğinde options'ları getir
  useEffect(() => {
    if (filter?.mainCategoryOptionId) {
      getOptionListByCategory(filter?.mainCategoryOptionId);
    } else {
      setOptions([]);
    }
  }, [filter?.mainCategoryOptionId]);

  // Panel kontrolü
  const toggleAllPanels = () => {
    if (isShowAllPanel) {
      setActiveKeys([]);
    } else {
      setActiveKeys(totalPanels);
    }
  };

  const handlePanelChange = (keys: string | string[]) => {
    setActiveKeys(Array.isArray(keys) ? keys : [keys]);
  };

  useEffect(() => {
    setIsShowAllPanel(activeKeys.length === totalPanels.length);
  }, [activeKeys]);

  // Key capitalize utility
  function capitalizeKeys<T extends Record<string, any>>(obj: T): Record<string, any> {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key.charAt(0).toUpperCase() + key.slice(1),
        value,
      ])
    );
  }

  // Merkezi debounced range handler
  const handleRangeOptionChange = useCallback((optionId: number, minValue: any, maxValue: any, title: string) => {
    // UI için hemen güncelle
    setRangeValues(prev => ({
      ...prev,
      [optionId]: { min: minValue, max: maxValue }
    }));

    // Mevcut timeout'u temizle
    if (debounceTimeouts.current[optionId]) {
      clearTimeout(debounceTimeouts.current[optionId]);
    }

    // Debounced selectedOptions güncellemesi
    debounceTimeouts.current[optionId] = setTimeout(() => {
      setSelectedOptions((prevOptions: any[]) => {
        // Mevcut option'ı kaldır
        const filteredOptions = prevOptions.filter((option: any) => 
          option.key !== optionId
        );

        // Değer varsa yeni option ekle
        if ((minValue !== null && minValue !== undefined && minValue !== '') || 
            (maxValue !== null && maxValue !== undefined && maxValue !== '')) {
          const values = [];
          
          if (minValue !== null && minValue !== undefined && minValue !== '') {
            values.push({ key: `min_${minValue}`, value: minValue });
          }
          
          if (maxValue !== null && maxValue !== undefined && maxValue !== '') {
            values.push({ key: `max_${maxValue}`, value: maxValue });
          }

          const newOption = {
            key: optionId,
            values: values
          };
          
          return [...filteredOptions, newOption];
        } else {
          // Değer yoksa sadece kaldır
          return filteredOptions;
        }
      });
    }, 500); // 500ms debounce - daha kararlı
  }, []);

  // Component unmount'ta timeout'ları temizle
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Form submit handler
  const handleOnFinish = async () => {
    let formValues: any = form.getFieldsValue();
    let currentFilter = { ...filter, IsSearchFilter: false };

    // Price parsing
    formValues["MinPrice"] = formValues["MinPrice"]
      ? parsePrice(formValues["MinPrice"])
      : null;
    formValues["MaxPrice"] = formValues["MaxPrice"]
      ? parsePrice(formValues["MaxPrice"])
      : null;

    // Currency handling
    if (currencySegmentValue) {
      let allData = currencies?.data?.data || [];
      let findItem = allData.find((item: any) => item.id === currencySegmentValue);
      if (findItem) {
        formValues["currencyName"] = findItem.shortName;
        formValues["currencyId"] = currencySegmentValue;
      }
    }

    // Boş değerleri temizle
    for (let key in formValues) {
      if (!formValues[key] || 
          (Array.isArray(formValues[key]) && formValues[key].length === 0)) {
        delete formValues[key];
        delete currentFilter[key];
      }
    }

    formValues = capitalizeKeys(formValues);

    // Filter'ı birleştir
    let resultFilter = {
      ...currentFilter,
      ...formValues,
    };

    // SelectedOptions işlemi
    if (selectedOptions?.length > 0) {
      let optionString = "";

      selectedOptions.forEach((option: any) => {
        const optionKey = option.key;
        if (option?.values?.length > 0) {
          option.values.forEach((value: any) => {
            if (optionString) {
              optionString += ",";
            }
            optionString += `${optionKey}:${value?.value || value?.key || value}`;
          });
        } else {
          if (optionString) {
            optionString += ",";
          }
          optionString += `${option?.key}:${option?.value}`;
        }
      });

      resultFilter = {
        ...resultFilter,
        SelectedOptionList: optionString,
        selectedOptionListObj: selectedOptions
      };
    } else {
      // SelectedOptions yoksa bu alanları kaldır
      const { SelectedOptionList, selectedOptionListObj, ...restFilter } = resultFilter;
      resultFilter = restFilter;
    }

    await dispatch(handleSetAdvertListFilter({ filter: resultFilter }));
  };

  // Form değerlerini filter'dan güncelle
  useEffect(() => {
    form.setFieldsValue({
      cityId: filter?.CityId || null,
      Title: filter?.Title,
      districtIds: filter?.DistrictIds || null,
      neighbourhoodIds: filter?.NeighbourhoodIds || null,
      MinPrice: filter?.MinPrice || null,
      MaxPrice: filter?.MaxPrice || null,
    });

    if (!filter?.MinPrice) {
      form.resetFields(["MinPrice"]);
    }
    if (!filter?.MaxPrice) {
      form.resetFields(["MaxPrice"]);
    }
  }, [filter, form]);

  // Filter'dan selectedOptions'ları güncelle
  useEffect(() => {
    if (filter?.selectedOptionListObj?.length > 0) {
      setSelectedOptions(filter?.selectedOptionListObj);
      
      // Range değerlerini de güncelle
      const newRangeValues: Record<number, { min: any, max: any }> = {};
      filter.selectedOptionListObj.forEach((option: any) => {
        if (option.values && option.values.length > 0) {
          const minVal = option.values.find((v: any) => v.key?.toString().startsWith('min_'));
          const maxVal = option.values.find((v: any) => v.key?.toString().startsWith('max_'));
          
          if (minVal || maxVal) {
            newRangeValues[option.key] = {
              min: minVal?.value || null,
              max: maxVal?.value || null
            };
          }
        }
      });
      setRangeValues(newRangeValues);
    } else {
      setSelectedOptions([]);
      setRangeValues({});
    }
  }, [filter?.selectedOptionListObj]);

  // Filter temizleme
  const handleClearFilters = () => {
    // Tüm timeout'ları temizle
    Object.values(debounceTimeouts.current).forEach(timeout => {
      if (timeout) clearTimeout(timeout);
    });
    
    // State'leri sıfırla
     setSelectedOptions([]);
    setRangeValues({});
    
    // Redux'ta filter'ı sıfırla
    dispatch(handleResetAdvertListFilter());
  };

  return (
    <MazakaForm
      form={form}
      initialValues={{ currencyId: 1, currencyName: "TL" }}
      submitButtonVisible={false}
      onFinish={handleOnFinish}
    >
      <Row gutter={[0, 10]}>
        <Col xs={24}>
          {Object.entries(filter).length > 13 && (
            <Button
              icon={<ClearOutlined />}
              type="text"
              className="!text-red-500 hover:!bg-red-500 hover:!text-white"
              onClick={handleClearFilters}
            >
              Filtreleri Temizle
            </Button>
          )}
        </Col>
        
        <Col
          xs={24}
          className={`${
            filter?.CategoryId === 502 || filter?.CategoryId === 1
              ? ""
              : "!max-h-screen !overflow-auto"
          }`}
        >
          <Collapse
            activeKey={activeKeys}
            onChange={handlePanelChange}
            className="bg-transparent border-none !border-t-0"
            size="small"
            style={{ borderTop: '0px' }}
          >
            <Panel 
              header="Arama" 
              key="1" 
              className="!bg-[#fff] !bg-white !rounded-lg !border !border-gray-200 !shadow-sm mb-3"
            >
              <Row gutter={[0, 10]} className="!rounded-lg" style={{ borderTop: '0px' }}>
                <Search />
              </Row>
            </Panel>

            <Panel 
              header="Para Birimi" 
              key="2" 
              className="!bg-[#fff] !bg-white !rounded-lg !border !border-gray-200 !shadow-sm mb-3"
            >
              <Row gutter={[0, 10]} className="">
                <Col xs={24}>
                  <Pricing
                    segmentValue={currencySegmentValue}
                    setSegmentValue={setCurrencySegmentValue}
                  />
                </Col>
              </Row>
            </Panel>

            {filter?.categoryType === 0 && (
              <>
                <Panel 
                  header="m² (Brüt)" 
                  key="3" 
                  className="!bg-[#fff] !bg-white !rounded-lg !border !border-gray-200 !shadow-sm mb-3"
                >
                  <Row gutter={8}>
                    {/* <Col span={12}>
                      <Input
                        placeholder="Min m² (Brüt)"
                        value={rangeValues[216]?.min || ''}
                        onChange={(e) => handleRangeOptionChange(216, e.target.value || null, rangeValues[216]?.max, "m² (Brüt)")}
                        type="number"
                      />
                    </Col> */}
                    <Col span={24}>
                      <Input
                        placeholder="(Brüt)  m² "
                        value={rangeValues[216]?.max || ''}
                        onChange={(e) => handleRangeOptionChange(216, rangeValues[216]?.min, e.target.value || null, "m² (Brüt)")}
                        type="number"
                      />
                    </Col>
                  </Row>
                </Panel>
                
                <Panel 
                  header="m² (Net)" 
                  key="4" 
                  className="!bg-[#fff] !bg-white !rounded-lg !border !border-gray-200 !shadow-sm mb-3"
                >
                  <Row gutter={8}>
                    {/* <Col span={12}>
                      <Input
                        placeholder="Min m² (Net)"
                        value={rangeValues[217]?.min || ''}
                        onChange={(e) => handleRangeOptionChange(217, e.target.value || null, rangeValues[217]?.max, "m² (Net)")}
                        type="number"
                      />
                    </Col> */}
                    <Col span={24}>
                      <Input
                        placeholder="(Net) m²"
                        value={rangeValues[217]?.max || ''}
                        onChange={(e) => handleRangeOptionChange(217, rangeValues[217]?.min, e.target.value || null, "m² (Net)")}
                        type="number"
                      />
                    </Col>
                  </Row>
                </Panel>
              </>
            )}

            {filter?.categoryType === 1 && (
              <>
                <Panel 
                  header="KM" 
                  key="5" 
                  className="!bg-[#fff] !bg-white !rounded-lg !border !border-gray-200 !shadow-sm mb-3"
                >
                  <Row gutter={8}>
                    <Col span={12}>
                      <Input
                        placeholder="Min KM"
                        value={rangeValues[201]?.min || ''}
                        onChange={(e) => handleRangeOptionChange(201, e.target.value || null, rangeValues[201]?.max, "KM")}
                        type="number"
                      />
                    </Col>
                    <Col span={12}>
                      <Input
                        placeholder="Max KM"
                        value={rangeValues[201]?.max || ''}
                        onChange={(e) => handleRangeOptionChange(201, rangeValues[201]?.min, e.target.value || null, "KM")}
                        type="number"
                      />
                    </Col>
                  </Row>
                </Panel>
                
                <Panel 
                  header="Yıl" 
                  key="6" 
                  className="!bg-[#fff] !bg-white !rounded-lg !border !border-gray-200 !shadow-sm mb-3"
                >
                  <Row gutter={[0, 10]} className="">
                    <Col xs={24}>
                      <RangeOptionsFilter
                        isMobile={isMobile}
                        minName="minYear"
                        maxName="maxYear"
                        title="Yıl"
                        optionId={202}
                        onRangeChange={handleRangeOptionChange}
                        minValue={rangeValues[202]?.min}
                        maxValue={rangeValues[202]?.max}
                      />
                    </Col>
                  </Row>
                </Panel>
              </>
            )}

            {options
              ?.sort((a: any, b: any) => a?.order - b?.order)
              .map((item: any) => (
                <Panel 
                  header={item.option.name} 
                  key={item.option.name} 
                  className="!bg-white !rounded-lg !border !border-gray-200 !shadow-sm mb-3"
                >
                  <Col xs={24}>
                    <BaseOptionItem
                      item={item}
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                    />
                  </Col>
                </Panel>
              ))}
          </Collapse>
        </Col>
        
        <Col span={24}>
          <ScrollButton options={options} isMobile={isMobile} />
        </Col>
      </Row>
    </MazakaForm>
  );
};

export default LeftDetailsFilter;